import {
  Alert,
  Box,
  Button,
  Container,
  IconButton,
  InputAdornment,
  Paper,
  Snackbar,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { tokens } from "../../theme";
import { motion } from "framer-motion";
import TopBarOfMarketing from "../../components/TopBarOfMarketing";
import Footer from "../../components/Footer";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import LockIcon from "@mui/icons-material/Lock";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import TenantService from "../../services/tenantService";

export default function TenantResetPassword({ darkMode, setDarkMode }) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();
  const location = useLocation();
  const tenantService = new TenantService();
  
  const resetToken = location.state?.resetToken;
  const email = location.state?.email;
  
  const [formData, setFormData] = useState({
    newPassword: "",
    confirmPassword: "",
  });
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  // Reset token yoksa geri yönlendir
  useEffect(() => {
    if (!resetToken) {
      navigate("/forgot-password/tenant");
    }
  }, [resetToken, navigate]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const validatePassword = (password) => {
    if (password.length < 6) {
      return "Password must be at least 6 characters";
    }
    return "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    
    // Validation
    if (!formData.newPassword || !formData.confirmPassword) {
      setError("Please fill in all fields");
      return;
    }

    const passwordError = validatePassword(formData.newPassword);
    if (passwordError) {
      setError(passwordError);
      return;
    }
    
    if (formData.newPassword !== formData.confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    
    try {
      setLoading(true);
      await tenantService.resetPassword({
        resetToken,
        newPassword: formData.newPassword,
        confirmPassword: formData.confirmPassword,
      });
      
      setSnackbar({
        open: true,
        message: "Password successfully changed!",
        severity: "success",
      });
      
      // 2 saniye sonra login sayfasına yönlendir
      setTimeout(() => {
        navigate("/corporate-login");
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.error || "An error occurred while resetting password");
      setSnackbar({
        open: true,
        message: err.response?.data?.error || "An error occurred",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ 
      width: "100%",
      background: darkMode ? colors.primary[500] : 'white',
      color: darkMode ? 'white' : colors.grey[100],
    }}>
      <TopBarOfMarketing darkMode={darkMode} setDarkMode={setDarkMode} />
      <Box
        sx={{
          minHeight: "100vh",
          background: darkMode 
            ? `linear-gradient(135deg, ${colors.primary[600]}, ${colors.greenAccent[700]})`
            : `linear-gradient(135deg, ${colors.primary[400]}, ${colors.greenAccent[500]})`,
          display: "flex",
          flexDirection: "column",
          mt: "64px",
        }}
      >
        <Container maxWidth="sm" sx={{ py: 8, flex: 1 }}>
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Paper
              elevation={3}
              sx={{
                p: 4,
                borderRadius: "24px",
                background: darkMode ? colors.primary[400] : "rgba(255, 255, 255, 0.95)",
                backdropFilter: "blur(10px)",
                boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
                border: "1px solid rgba(255, 255, 255, 0.1)",
                color: darkMode ? 'white' : colors.grey[100],
                transition: "transform 0.3s ease",
                "&:hover": {
                  transform: "translateY(-5px)",
                  boxShadow: "0 12px 40px rgba(0,0,0,0.15)",
                },
              }}
            >
              <Box sx={{ textAlign: "center", mb: 3 }}>
                <CheckCircleIcon sx={{ fontSize: 60, color: colors.greenAccent[500], mb: 2 }} />
                <Typography
                  variant="h3"
                  sx={{
                    mb: 1,
                    fontFamily: "'Poppins', sans-serif",
                    fontWeight: 600,
                    background: `linear-gradient(135deg, ${colors.greenAccent[500]}, ${colors.blueAccent[400]})`,
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  Set New Password
                </Typography>
                {email && (
                  <Typography
                    variant="body2"
                    sx={{
                      color: darkMode ? colors.grey[300] : colors.grey[600],
                    }}
                  >
                    {email}
                  </Typography>
                )}
              </Box>

              <form onSubmit={handleSubmit}>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" sx={{ mb: 1, color: darkMode ? "white" : colors.grey[900] }}>
                    New Password
                  </Typography>
                  <TextField
                    fullWidth
                    required
                    name="newPassword"
                    type={showNewPassword ? "text" : "password"}
                    value={formData.newPassword}
                    onChange={handleChange}
                    placeholder="At least 6 characters"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockIcon sx={{ color: darkMode ? "rgba(255, 255, 255, 0.7)" : colors.grey[500] }} />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowNewPassword(!showNewPassword)}
                            edge="end"
                          >
                            {showNewPassword ? (
                              <VisibilityOffIcon sx={{ color: darkMode ? "rgba(255, 255, 255, 0.7)" : colors.grey[500] }} />
                            ) : (
                              <VisibilityIcon sx={{ color: darkMode ? "rgba(255, 255, 255, 0.7)" : colors.grey[500] }} />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "12px",
                        "&:hover fieldset": {
                          borderColor: colors.greenAccent[500],
                        },
                      },
                      "& .MuiInputBase-input": {
                        color: darkMode ? "white" : colors.grey[900],
                      },
                    }}
                  />
                </Box>

                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" sx={{ mb: 1, color: darkMode ? "white" : colors.grey[900] }}>
                    Confirm New Password
                  </Typography>
                  <TextField
                    fullWidth
                    required
                    name="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    placeholder="Re-enter your password"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockIcon sx={{ color: darkMode ? "rgba(255, 255, 255, 0.7)" : colors.grey[500] }} />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            edge="end"
                          >
                            {showConfirmPassword ? (
                              <VisibilityOffIcon sx={{ color: darkMode ? "rgba(255, 255, 255, 0.7)" : colors.grey[500] }} />
                            ) : (
                              <VisibilityIcon sx={{ color: darkMode ? "rgba(255, 255, 255, 0.7)" : colors.grey[500] }} />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "12px",
                        "&:hover fieldset": {
                          borderColor: colors.greenAccent[500],
                        },
                      },
                      "& .MuiInputBase-input": {
                        color: darkMode ? "white" : colors.grey[900],
                      },
                    }}
                  />
                </Box>

                {error && (
                  <Typography
                    color="error"
                    variant="body2"
                    sx={{ mb: 2, textAlign: "center" }}
                  >
                    {error}
                  </Typography>
                )}

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={loading}
                  sx={{
                    background: "white",
                    color: colors.greenAccent[600],
                    borderRadius: "12px",
                    padding: "12px",
                    fontSize: "1rem",
                    fontWeight: 600,
                    textTransform: "none",
                    boxShadow: "0 4px 14px rgba(0,0,0,0.1)",
                    "&:hover": {
                      background: "rgba(255,255,255,0.95)",
                      transform: "translateY(-2px)",
                      boxShadow: "0 6px 20px rgba(0,0,0,0.15)",
                    },
                    "&:disabled": {
                      background: colors.grey[500],
                      color: colors.grey[300],
                    },
                  }}
                >
                  {loading ? "Saving..." : "Change Password"}
                </Button>
              </form>
            </Paper>
          </motion.div>
        </Container>

        <Footer />

        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            variant="filled"
            sx={{
              width: "100%",
              borderRadius: "12px",
            }}
          >
            <Typography variant="body1" color="white">
              {snackbar.message}
            </Typography>
          </Alert>
        </Snackbar>
      </Box>
    </Box>
  );
}








